import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import {
  BarChart3, TrendingUp, Clock, AlertTriangle, Calendar, Users,
  ArrowUpRight, ArrowDownRight, Timer, RefreshCw,
  Activity, Briefcase,
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, PointElement, LineElement,
  ArcElement, Title, Tooltip, Legend, Filler,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { format, subDays, eachDayOfInterval, startOfWeek } from 'date-fns';
import LoadingSpinner from '../components/LoadingSpinner';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, PointElement, LineElement,
  ArcElement, Title, Tooltip, Legend, Filler
);

const CHART_COLORS = ['#2563EB', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#06B6D4', '#EC4899', '#14B8A6', '#F97316'];
const CHART_GRID = { color: 'rgba(255,255,255,0.06)' };
const CHART_TICK = { color: '#9ca3af' };

const darkChartOptions = (extra = {}) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false }, ...extra.plugins },
  scales: {
    y: { beginAtZero: true, ticks: { ...CHART_TICK, ...extra.yTicks }, grid: CHART_GRID },
    x: { ticks: { ...CHART_TICK, ...extra.xTicks }, grid: CHART_GRID },
    ...extra.scales,
  },
  ...extra.root,
});

const TABS = ['overview', 'timeTracking', 'staffInsights', 'scheduling'];

export default function Analytics({ user, userData, companyId }) {
  const { t } = useTranslation();
  const [period, setPeriod] = useState('month');
  const [activeTab, setActiveTab] = useState('overview');
  const [shifts, setShifts] = useState([]);
  const [swaps, setSwaps] = useState([]);
  const [staff, setStaff] = useState([]);
  const [timeRecords, setTimeRecords] = useState([]);
  const [groups, setGroups] = useState([]); // eslint-disable-line no-unused-vars
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const daysBack = period === 'week' ? 7 : period === 'month' ? 30 : 90;
  const sinceDate = format(subDays(new Date(), daysBack), 'yyyy-MM-dd');

  const loadData = async (showRefresh = false) => {
    if (!companyId) return;
    if (showRefresh) setRefreshing(true); else setLoading(true);
    try {
      const [shiftsSnap, swapsSnap, staffSnap, timeSnap, groupsSnap] = await Promise.all([
        getDocs(query(
          collection(db, 'companies', companyId, 'shifts'),
          where('date', '>=', sinceDate),
          orderBy('date'), orderBy('startTime')
        )),
        getDocs(query(
          collection(db, 'companies', companyId, 'swapRequests'),
          where('createdAt', '>=', sinceDate)
        )),
        getDocs(collection(db, 'companies', companyId, 'users')),
        getDocs(query(
          collection(db, 'companies', companyId, 'timeRecords'),
          where('date', '>=', sinceDate),
          where('status', '==', 'completed'),
          orderBy('date'), orderBy('clockIn')
        )),
        getDocs(collection(db, 'companies', companyId, 'shiftsGroups')),
      ]);
      setShifts(shiftsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setSwaps(swapsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setStaff(staffSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setTimeRecords(timeSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setGroups(groupsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Analytics load error:', err);
    }
    setLoading(false);
    setRefreshing(false);
  };

  useEffect(() => { loadData(); }, [companyId, period]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ─── Derived Stats ─── */
  const stats = useMemo(() => {
    const totalShifts = shifts.length;
    const filledShifts = shifts.filter(s => s.assignedStaffId || s.assignedTo).length;
    const coverageRate = totalShifts > 0 ? ((filledShifts / totalShifts) * 100) : 0;
    const confirmedShifts = shifts.filter(s => s.status === 'confirmed').length;
    const confirmRate = totalShifts > 0 ? ((confirmedShifts / totalShifts) * 100) : 0;

    const scheduledHours = shifts.reduce((sum, s) => {
      if (!s.startTime || !s.endTime) return sum;
      const [sh, sm] = s.startTime.split(':').map(Number);
      const [eh, em] = s.endTime.split(':').map(Number);
      let mins = (eh * 60 + em) - (sh * 60 + sm);
      if (mins < 0) mins += 1440;
      return sum + mins / 60;
    }, 0);

    const clockedHours = timeRecords.reduce((sum, r) => sum + (r.duration || 0), 0) / 60;

    const activeStaff = staff.filter(s => s.active !== false).length;
    const pendingSwaps = swaps.filter(s => s.status === 'pending').length;
    const approvedSwaps = swaps.filter(s => s.status === 'approved').length;
    const rejectedSwaps = swaps.filter(s => s.status === 'rejected').length;
    const swapApprovalRate = swaps.length > 0 ? ((approvedSwaps / swaps.length) * 100) : 0;

    const avgShiftsPerDay = totalShifts / Math.max(daysBack, 1);
    const avgHoursPerStaff = activeStaff > 0 ? scheduledHours / activeStaff : 0;

    return {
      totalShifts, filledShifts, coverageRate, confirmRate,
      scheduledHours, clockedHours, activeStaff,
      pendingSwaps, approvedSwaps, rejectedSwaps, swapApprovalRate,
      avgShiftsPerDay, avgHoursPerStaff,
    };
  }, [shifts, swaps, staff, timeRecords, daysBack]);

  /* ─── Chart: Shifts per Day ─── */
  const shiftsPerDayData = useMemo(() => {
    const now = new Date();
    const dates = eachDayOfInterval({ start: subDays(now, daysBack - 1), end: now });
    const labels = dates.map(d => format(d, daysBack <= 7 ? 'EEE' : daysBack <= 30 ? 'dd MMM' : 'dd/MM'));
    const shiftCounts = dates.map(d => {
      const dateStr = format(d, 'yyyy-MM-dd');
      return shifts.filter(s => s.date === dateStr).length;
    });
    const clockCounts = dates.map(d => {
      const dateStr = format(d, 'yyyy-MM-dd');
      return timeRecords.filter(r => r.date === dateStr).length;
    });
    return {
      labels,
      datasets: [
        { label: t('analytics.scheduledShifts'), data: shiftCounts, backgroundColor: '#2563EB', borderRadius: 4 },
        { label: t('analytics.clockIns'), data: clockCounts, backgroundColor: '#10B981', borderRadius: 4 },
      ],
    };
  }, [shifts, timeRecords, daysBack, t]);

  /* ─── Chart: Hours per Day (scheduled vs clocked) ─── */
  const hoursPerDayData = useMemo(() => {
    const now = new Date();
    const dates = eachDayOfInterval({ start: subDays(now, daysBack - 1), end: now });
    const labels = dates.map(d => format(d, daysBack <= 7 ? 'EEE' : daysBack <= 30 ? 'dd MMM' : 'dd/MM'));

    const scheduledPerDay = dates.map(d => {
      const dateStr = format(d, 'yyyy-MM-dd');
      return shifts.filter(s => s.date === dateStr).reduce((sum, s) => {
        if (!s.startTime || !s.endTime) return sum;
        const [sh, sm] = s.startTime.split(':').map(Number);
        const [eh, em] = s.endTime.split(':').map(Number);
        let mins = (eh * 60 + em) - (sh * 60 + sm);
        if (mins < 0) mins += 1440;
        return sum + mins / 60;
      }, 0);
    });

    const clockedPerDay = dates.map(d => {
      const dateStr = format(d, 'yyyy-MM-dd');
      return timeRecords.filter(r => r.date === dateStr).reduce((sum, r) => sum + (r.duration || 0) / 60, 0);
    });

    return {
      labels,
      datasets: [
        {
          label: t('analytics.scheduledHours'),
          data: scheduledPerDay,
          borderColor: '#2563EB',
          backgroundColor: 'rgba(37, 99, 235, 0.1)',
          fill: true, tension: 0.3,
          pointRadius: daysBack <= 7 ? 4 : 2,
        },
        {
          label: t('analytics.clockedHours'),
          data: clockedPerDay,
          borderColor: '#10B981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          fill: true, tension: 0.3,
          pointRadius: daysBack <= 7 ? 4 : 2,
        },
      ],
    };
  }, [shifts, timeRecords, daysBack, t]);

  /* ─── Chart: Hours per Staff (top 10) ─── */
  const hoursPerStaffData = useMemo(() => {
    const staffHours = {};
    shifts.forEach(s => {
      const sid = s.assignedStaffId || s.assignedTo;
      if (!sid || !s.startTime || !s.endTime) return;
      const [sh, sm] = s.startTime.split(':').map(Number);
      const [eh, em] = s.endTime.split(':').map(Number);
      let mins = (eh * 60 + em) - (sh * 60 + sm);
      if (mins < 0) mins += 1440;
      if (!staffHours[sid]) staffHours[sid] = { scheduled: 0, clocked: 0 };
      staffHours[sid].scheduled += mins / 60;
    });
    timeRecords.forEach(r => {
      if (!r.userId) return;
      if (!staffHours[r.userId]) staffHours[r.userId] = { scheduled: 0, clocked: 0 };
      staffHours[r.userId].clocked += (r.duration || 0) / 60;
    });

    const sorted = Object.entries(staffHours)
      .sort(([, a], [, b]) => (b.scheduled + b.clocked) - (a.scheduled + a.clocked))
      .slice(0, 10);

    const getStaffName = (id) => {
      const member = staff.find(s => s.id === id);
      return member?.displayName || member?.username || id.slice(0, 8);
    };

    return {
      labels: sorted.map(([id]) => getStaffName(id)),
      datasets: [
        { label: t('analytics.scheduledHours'), data: sorted.map(([, h]) => +h.scheduled.toFixed(1)), backgroundColor: '#2563EB', borderRadius: 4 },
        { label: t('analytics.clockedHours'), data: sorted.map(([, h]) => +h.clocked.toFixed(1)), backgroundColor: '#10B981', borderRadius: 4 },
      ],
    };
  }, [shifts, timeRecords, staff, t]);

  /* ─── Chart: Shift Status Breakdown ─── */
  const shiftStatusData = useMemo(() => {
    const statusCounts = {};
    shifts.forEach(s => {
      const st = s.status || 'unassigned';
      statusCounts[st] = (statusCounts[st] || 0) + 1;
    });
    const statusColors = {
      assigned: '#3B82F6', confirmed: '#10B981', unassigned: '#F59E0B',
      declined: '#EF4444', cancelled: '#6B7280',
    };
    const entries = Object.entries(statusCounts);
    return {
      labels: entries.map(([k]) => t(`analytics.status_${k}`, k.charAt(0).toUpperCase() + k.slice(1))),
      datasets: [{
        data: entries.map(([, v]) => v),
        backgroundColor: entries.map(([k]) => statusColors[k] || '#8B5CF6'),
      }],
    };
  }, [shifts, t]);

  /* ─── Chart: Staff Role Distribution ─── */
  const roleData = useMemo(() => {
    const roles = {};
    staff.filter(s => s.active !== false).forEach(s => {
      const role = s.role || 'staff';
      roles[role] = (roles[role] || 0) + 1;
    });
    const entries = Object.entries(roles);
    return {
      labels: entries.map(([r]) => r.charAt(0).toUpperCase() + r.slice(1)),
      datasets: [{
        data: entries.map(([, v]) => v),
        backgroundColor: CHART_COLORS.slice(0, entries.length),
      }],
    };
  }, [staff]);

  /* ─── Chart: Group/Location Breakdown ─── */
  const groupData = useMemo(() => {
    const groupCounts = {};
    const groupClocked = {};
    shifts.forEach(s => {
      const gn = s.groupName || t('analytics.noGroup');
      groupCounts[gn] = (groupCounts[gn] || 0) + 1;
    });
    timeRecords.forEach(r => {
      const gn = r.groupName || t('analytics.noGroup');
      groupClocked[gn] = (groupClocked[gn] || 0) + (r.duration || 0) / 60;
    });
    const allGroups = [...new Set([...Object.keys(groupCounts), ...Object.keys(groupClocked)])];
    return {
      labels: allGroups,
      datasets: [
        { label: t('analytics.shifts'), data: allGroups.map(g => groupCounts[g] || 0), backgroundColor: '#2563EB', borderRadius: 4 },
        { label: t('analytics.clockedHoursShort'), data: allGroups.map(g => +(groupClocked[g] || 0).toFixed(1)), backgroundColor: '#10B981', borderRadius: 4 },
      ],
    };
  }, [shifts, timeRecords, t]);

  /* ─── Chart: Punctuality — avg clock-in deviation per day ─── */
  const punctualityData = useMemo(() => {
    const deviations = {};
    timeRecords.forEach(r => {
      if (!r.clockIn || !r.date) return;
      const matchedShift = shifts.find(s =>
        s.date === r.date && (s.assignedStaffId === r.userId || s.assignedTo === r.userId)
      );
      if (!matchedShift || !matchedShift.startTime) return;
      const clockInDate = new Date(r.clockIn);
      const [sh, sm] = matchedShift.startTime.split(':').map(Number);
      const scheduledMins = sh * 60 + sm;
      const actualMins = clockInDate.getHours() * 60 + clockInDate.getMinutes();
      const deviation = actualMins - scheduledMins;
      if (!deviations[r.date]) deviations[r.date] = [];
      deviations[r.date].push(deviation);
    });
    const sortedDates = Object.keys(deviations).sort();
    const labels = sortedDates.map(d => {
      try { return format(new Date(d + 'T00:00:00'), daysBack <= 7 ? 'EEE' : 'dd MMM'); }
      catch { return d; }
    });
    const avgDevs = sortedDates.map(d => {
      const devs = deviations[d];
      return +(devs.reduce((a, b) => a + b, 0) / devs.length).toFixed(1);
    });
    return {
      labels,
      datasets: [{
        label: t('analytics.avgDeviation'),
        data: avgDevs,
        borderColor: '#F59E0B',
        backgroundColor: avgDevs.map(v => v > 0 ? 'rgba(239,68,68,0.3)' : 'rgba(16,185,129,0.3)'),
        fill: false, tension: 0.3, pointRadius: 4,
        pointBackgroundColor: avgDevs.map(v => v > 0 ? '#EF4444' : '#10B981'),
      }],
    };
  }, [shifts, timeRecords, daysBack, t]);

  /* ─── Chart: Weekly Trend ─── */
  const weeklyTrendData = useMemo(() => {
    if (daysBack < 14) return null;
    const weekMap = {};
    shifts.forEach(s => {
      if (!s.date) return;
      try {
        const ws = format(startOfWeek(new Date(s.date + 'T00:00:00'), { weekStartsOn: 1 }), 'dd MMM');
        weekMap[ws] = (weekMap[ws] || 0) + 1;
      } catch { /* skip */ }
    });
    const weeks = Object.keys(weekMap);
    return {
      labels: weeks,
      datasets: [{
        label: t('analytics.shiftsPerWeek'),
        data: weeks.map(w => weekMap[w]),
        borderColor: '#8B5CF6',
        backgroundColor: 'rgba(139,92,246,0.1)',
        fill: true, tension: 0.3, pointRadius: 4,
      }],
    };
  }, [shifts, daysBack, t]);

  /* ─── Insights ─── */
  const insights = useMemo(() => {
    const dayTotals = [0, 0, 0, 0, 0, 0, 0];
    shifts.forEach(s => {
      if (!s.date) return;
      try { dayTotals[new Date(s.date + 'T00:00:00').getDay()]++; } catch { /* skip */ }
    });
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const busiestDay = dayNames[dayTotals.indexOf(Math.max(...dayTotals))];

    const hourTotals = new Array(24).fill(0);
    shifts.forEach(s => {
      if (!s.startTime) return;
      const h = parseInt(s.startTime.split(':')[0]);
      if (!isNaN(h)) hourTotals[h]++;
    });
    const peakHour = `${String(hourTotals.indexOf(Math.max(...hourTotals))).padStart(2, '0')}:00`;

    const staffMap = {};
    staff.forEach(s => { staffMap[s.id] = s; });
    const clockedByStaff = {};
    timeRecords.forEach(r => {
      if (!r.userId) return;
      clockedByStaff[r.userId] = (clockedByStaff[r.userId] || 0) + (r.duration || 0) / 60;
    });
    const overtimeRisk = [];
    Object.entries(clockedByStaff).forEach(([id, hours]) => {
      const member = staffMap[id];
      if (member?.contractHours && hours > member.contractHours * (daysBack / 7)) {
        overtimeRisk.push({ name: member.displayName || member.username, hours: +hours.toFixed(1), contract: +(member.contractHours * (daysBack / 7)).toFixed(0) });
      }
    });

    return { busiestDay, peakHour, overtimeRisk };
  }, [shifts, timeRecords, staff, daysBack]);

  /* ─── Render Helpers ─── */
  const StatCard = ({ icon: Icon, label, value, sub, color = 'text-primary', trend }) => (
    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5 hover:bg-white/[0.07] transition-colors">
      <div className="flex items-center justify-between mb-3">
        <Icon size={20} className={color} />
        {trend !== undefined && (
          trend >= 0
            ? <span className="flex items-center gap-1 text-xs text-green-400"><ArrowUpRight size={14} />{trend.toFixed(1)}%</span>
            : <span className="flex items-center gap-1 text-xs text-red-400"><ArrowDownRight size={14} />{Math.abs(trend).toFixed(1)}%</span>
        )}
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-sm text-gray-400 mt-0.5">{label}</p>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </div>
  );

  const ChartCard = ({ title, children, className = '', span = '' }) => (
    <div className={`bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 ${span} ${className}`}>
      <h3 className="font-semibold text-white mb-4">{title}</h3>
      <div className="relative h-64">{children}</div>
    </div>
  );

  if (loading) return <LoadingSpinner />;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <BarChart3 size={24} /> {t('analytics.title')}
          </h1>
          <p className="text-gray-400 mt-1">{t('analytics.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => loadData(true)}
            disabled={refreshing}
            className="p-2 rounded-lg bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-50"
            title={t('analytics.refresh')}
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
          </button>
          <div className="flex gap-1 bg-white/10 rounded-lg p-1">
            {[
              { id: 'week', label: '7d' },
              { id: 'month', label: '30d' },
              { id: 'quarter', label: '90d' },
            ].map(p => (
              <button
                key={p.id}
                onClick={() => setPeriod(p.id)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  period === p.id ? 'bg-primary text-white' : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white/5 rounded-lg p-1 mb-8 overflow-x-auto">
        {TABS.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === tab ? 'bg-primary text-white' : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            {t(`analytics.tab_${tab}`)}
          </button>
        ))}
      </div>

      {/* ════════════════════ OVERVIEW TAB ════════════════════ */}
      {activeTab === 'overview' && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Calendar} label={t('analytics.totalShifts')} value={stats.totalShifts}
              sub={`~${stats.avgShiftsPerDay.toFixed(1)} ${t('analytics.perDay')}`} />
            <StatCard icon={TrendingUp} label={t('analytics.coverageRate')} value={`${stats.coverageRate.toFixed(1)}%`}
              color={stats.coverageRate >= 90 ? 'text-green-400' : stats.coverageRate >= 70 ? 'text-yellow-400' : 'text-red-400'} />
            <StatCard icon={Clock} label={t('analytics.scheduledHours')} value={stats.scheduledHours.toFixed(0)}
              sub={`${stats.clockedHours.toFixed(0)}h ${t('analytics.clocked')}`} />
            <StatCard icon={Users} label={t('analytics.activeStaff')} value={stats.activeStaff}
              sub={`${stats.avgHoursPerStaff.toFixed(1)}h ${t('analytics.avgPerStaff')}`} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <ChartCard title={t('analytics.shiftsPerDay')}>
              <Bar data={shiftsPerDayData} options={darkChartOptions({ plugins: { legend: { display: true, labels: { color: '#9ca3af' } } } })} />
            </ChartCard>
            <ChartCard title={t('analytics.hoursPerDay')}>
              <Line data={hoursPerDayData} options={darkChartOptions({ plugins: { legend: { display: true, labels: { color: '#9ca3af' } } } })} />
            </ChartCard>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <ChartCard title={t('analytics.shiftStatus')}>
              <div className="flex items-center justify-center h-full">
                <div className="w-52 h-52">
                  <Doughnut data={shiftStatusData} options={{
                    responsive: true, maintainAspectRatio: false,
                    plugins: { legend: { position: 'bottom', labels: { color: '#9ca3af', padding: 12 } } },
                  }} />
                </div>
              </div>
            </ChartCard>
            <ChartCard title={t('analytics.byLocation')} span="lg:col-span-2">
              <Bar data={groupData} options={darkChartOptions({ plugins: { legend: { display: true, labels: { color: '#9ca3af' } } } })} />
            </ChartCard>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <Activity size={18} className="text-purple-400" />
                <h4 className="font-medium text-white">{t('analytics.busiestDay')}</h4>
              </div>
              <p className="text-2xl font-bold text-purple-400">{insights.busiestDay}</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <Clock size={18} className="text-cyan-400" />
                <h4 className="font-medium text-white">{t('analytics.peakHour')}</h4>
              </div>
              <p className="text-2xl font-bold text-cyan-400">{insights.peakHour}</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={18} className="text-amber-400" />
                <h4 className="font-medium text-white">{t('analytics.swapsPending')}</h4>
              </div>
              <p className="text-2xl font-bold text-amber-400">{stats.pendingSwaps}</p>
              <p className="text-xs text-gray-500 mt-1">{stats.approvedSwaps} {t('analytics.approved')}, {stats.rejectedSwaps} {t('analytics.rejected')}</p>
            </div>
          </div>
        </>
      )}

      {/* ════════════════════ TIME TRACKING TAB ════════════════════ */}
      {activeTab === 'timeTracking' && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Timer} label={t('analytics.clockedHours')} value={stats.clockedHours.toFixed(1)} color="text-green-400" />
            <StatCard icon={Clock} label={t('analytics.scheduledHours')} value={stats.scheduledHours.toFixed(0)} color="text-blue-400" />
            <StatCard icon={Activity} label={t('analytics.clockIns')} value={timeRecords.length} color="text-cyan-400" />
            <StatCard icon={TrendingUp} label={t('analytics.confirmRate')} value={`${stats.confirmRate.toFixed(1)}%`}
              color={stats.confirmRate >= 80 ? 'text-green-400' : 'text-yellow-400'} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <ChartCard title={t('analytics.hoursPerDay')}>
              <Line data={hoursPerDayData} options={darkChartOptions({ plugins: { legend: { display: true, labels: { color: '#9ca3af' } } } })} />
            </ChartCard>
            <ChartCard title={t('analytics.punctuality')}>
              {punctualityData.labels.length > 0 ? (
                <Line data={punctualityData} options={darkChartOptions({
                  plugins: { legend: { display: false } },
                  scales: {
                    y: { beginAtZero: false, ticks: { ...CHART_TICK, callback: v => `${v > 0 ? '+' : ''}${v}m` }, grid: CHART_GRID },
                    x: { ticks: CHART_TICK, grid: CHART_GRID },
                  },
                })} />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">{t('analytics.noMatchData')}</div>
              )}
            </ChartCard>
          </div>

          {insights.overtimeRisk.length > 0 && (
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-6 mb-8">
              <h3 className="font-semibold text-amber-400 mb-4 flex items-center gap-2">
                <AlertTriangle size={18} /> {t('analytics.overtimeRisk')}
              </h3>
              <div className="space-y-2">
                {insights.overtimeRisk.map((r, i) => (
                  <div key={i} className="flex items-center justify-between bg-amber-500/10 rounded-lg px-4 py-3">
                    <span className="text-white font-medium">{r.name}</span>
                    <span className="text-amber-400 text-sm">{r.hours}h / {r.contract}h {t('analytics.contract')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <ChartCard title={t('analytics.clockedByLocation')} className="mb-8">
            <Bar data={{ labels: groupData.labels, datasets: [groupData.datasets[1]] }} options={darkChartOptions()} />
          </ChartCard>
        </>
      )}

      {/* ════════════════════ STAFF INSIGHTS TAB ════════════════════ */}
      {activeTab === 'staffInsights' && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Users} label={t('analytics.activeStaff')} value={stats.activeStaff} color="text-blue-400" />
            <StatCard icon={Clock} label={t('analytics.avgHoursPerStaff')} value={stats.avgHoursPerStaff.toFixed(1)} color="text-cyan-400" />
            <StatCard icon={TrendingUp} label={t('analytics.coverageRate')} value={`${stats.coverageRate.toFixed(1)}%`}
              color={stats.coverageRate >= 90 ? 'text-green-400' : 'text-yellow-400'} />
            <StatCard icon={Briefcase} label={t('analytics.roles')} value={[...new Set(staff.map(s => s.role))].length} color="text-purple-400" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <ChartCard title={t('analytics.hoursPerStaff')}>
              <Bar data={hoursPerStaffData} options={darkChartOptions({
                plugins: { legend: { display: true, labels: { color: '#9ca3af' } } },
                root: { indexAxis: 'y' },
                scales: {
                  x: { beginAtZero: true, ticks: CHART_TICK, grid: CHART_GRID },
                  y: { ticks: { ...CHART_TICK, font: { size: 11 } }, grid: CHART_GRID },
                },
              })} />
            </ChartCard>
            <ChartCard title={t('analytics.roleDistribution')}>
              <div className="flex items-center justify-center h-full">
                <div className="w-52 h-52">
                  <Doughnut data={roleData} options={{
                    responsive: true, maintainAspectRatio: false,
                    plugins: { legend: { position: 'bottom', labels: { color: '#9ca3af', padding: 12 } } },
                  }} />
                </div>
              </div>
            </ChartCard>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-8">
            <h3 className="font-semibold text-white mb-4">{t('analytics.staffLeaderboard')}</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-400 border-b border-white/10">
                    <th className="text-left py-3 px-2">#</th>
                    <th className="text-left py-3 px-2">{t('analytics.col_staff')}</th>
                    <th className="text-left py-3 px-2">{t('analytics.col_role')}</th>
                    <th className="text-right py-3 px-2">{t('analytics.col_shifts')}</th>
                    <th className="text-right py-3 px-2">{t('analytics.col_scheduled')}</th>
                    <th className="text-right py-3 px-2">{t('analytics.col_clocked')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const leaderboard = staff
                      .filter(s => s.active !== false)
                      .map(s => {
                        const sShifts = shifts.filter(sh => sh.assignedStaffId === s.id || sh.assignedTo === s.id);
                        const schedHours = sShifts.reduce((sum, sh) => {
                          if (!sh.startTime || !sh.endTime) return sum;
                          const [a, b] = sh.startTime.split(':').map(Number);
                          const [c, d] = sh.endTime.split(':').map(Number);
                          let m = (c * 60 + d) - (a * 60 + b); if (m < 0) m += 1440;
                          return sum + m / 60;
                        }, 0);
                        const clockHours = timeRecords.filter(r => r.userId === s.id).reduce((sum, r) => sum + (r.duration || 0) / 60, 0);
                        return { ...s, shiftCount: sShifts.length, schedHours, clockHours };
                      })
                      .sort((a, b) => b.schedHours - a.schedHours)
                      .slice(0, 20);
                    return leaderboard.map((s, i) => (
                      <tr key={s.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="py-3 px-2 text-gray-500">{i + 1}</td>
                        <td className="py-3 px-2 text-white font-medium">{s.displayName || s.username}</td>
                        <td className="py-3 px-2"><span className="px-2 py-0.5 rounded-full text-xs bg-white/10 text-gray-300">{s.role}</span></td>
                        <td className="py-3 px-2 text-right text-gray-300">{s.shiftCount}</td>
                        <td className="py-3 px-2 text-right text-blue-400">{s.schedHours.toFixed(1)}h</td>
                        <td className="py-3 px-2 text-right text-green-400">{s.clockHours.toFixed(1)}h</td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ════════════════════ SCHEDULING TAB ════════════════════ */}
      {activeTab === 'scheduling' && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Calendar} label={t('analytics.totalShifts')} value={stats.totalShifts} color="text-blue-400" />
            <StatCard icon={TrendingUp} label={t('analytics.coverageRate')} value={`${stats.coverageRate.toFixed(1)}%`}
              color={stats.coverageRate >= 90 ? 'text-green-400' : 'text-yellow-400'} />
            <StatCard icon={AlertTriangle} label={t('analytics.swapsPending')} value={stats.pendingSwaps} color="text-amber-400" />
            <StatCard icon={RefreshCw} label={t('analytics.swapApproval')} value={`${stats.swapApprovalRate.toFixed(1)}%`}
              color={stats.swapApprovalRate >= 70 ? 'text-green-400' : 'text-yellow-400'} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <ChartCard title={t('analytics.shiftsPerDay')}>
              <Bar data={shiftsPerDayData} options={darkChartOptions({ plugins: { legend: { display: true, labels: { color: '#9ca3af' } } } })} />
            </ChartCard>
            {weeklyTrendData && (
              <ChartCard title={t('analytics.weeklyTrend')}>
                <Line data={weeklyTrendData} options={darkChartOptions({ plugins: { legend: { display: false } } })} />
              </ChartCard>
            )}
          </div>

          <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-8">
            <h3 className="font-semibold text-white mb-4">{t('analytics.swapSummary')}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white/5 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-white">{swaps.length}</p>
                <p className="text-sm text-gray-400">{t('analytics.totalSwaps')}</p>
              </div>
              <div className="bg-amber-500/10 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-amber-400">{stats.pendingSwaps}</p>
                <p className="text-sm text-amber-400/80">{t('analytics.pending')}</p>
              </div>
              <div className="bg-green-500/10 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-green-400">{stats.approvedSwaps}</p>
                <p className="text-sm text-green-400/80">{t('analytics.approved')}</p>
              </div>
              <div className="bg-red-500/10 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-red-400">{stats.rejectedSwaps}</p>
                <p className="text-sm text-red-400/80">{t('analytics.rejected')}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <ChartCard title={t('analytics.shiftStatus')}>
              <div className="flex items-center justify-center h-full">
                <div className="w-52 h-52">
                  <Doughnut data={shiftStatusData} options={{
                    responsive: true, maintainAspectRatio: false,
                    plugins: { legend: { position: 'bottom', labels: { color: '#9ca3af', padding: 12 } } },
                  }} />
                </div>
              </div>
            </ChartCard>
            <ChartCard title={t('analytics.byLocation')} span="lg:col-span-2">
              <Bar data={groupData} options={darkChartOptions({ plugins: { legend: { display: true, labels: { color: '#9ca3af' } } } })} />
            </ChartCard>
          </div>
        </>
      )}
    </div>
  );
}
